import default_theme from "assets/themes/themes";

const FIELD_COLOR = default_theme.palette.other.chatGreen;

const hasValue = (value) => {
  if (value === null || value === undefined) return false;

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === "object") {
    return Object.keys(value).length > 0;
  }

  if (typeof value === "boolean") {
    return value;
  }

  return true;
};

const formatFieldName = (fieldName) => {
  return fieldName
    .replace(/^(lead_org_|categories_|rdd_|location_|enum_)/, "")
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const shouldExcludeField = (fieldName) => {
  const excludedFields = [
    "id",
    "entryId",
    "climatechange_mitigation_raw",
    "climatechange_adaptation_raw",
    "climatechange_mitigation_ids",
    "climatechange_adaptation_ids",
    "rdd_raw",
    "priorities_ids",
    "prioritiesIds",
    "project_id",
    "author_id",
    "published_at",
    "lead_org_type"
  ];

  if (fieldName.endsWith("_raw")) {
    return true;
  }

  if (
    fieldName.toLowerCase().includes("priorities") &&
    fieldName.toLowerCase().includes("id")
  ) {
    return true;
  }

  if (
    fieldName.endsWith("_ids") &&
    (fieldName.includes("climatechange") ||
      fieldName.includes("priorities"))
  ) {
    return true;
  }

  return excludedFields.includes(fieldName);
};

const groupFieldsDynamically = (data) => {
  const fieldGroups = {};

  if (!data || typeof data !== "object") {
    return fieldGroups;
  }

  const priorityFields = ["aims", "story", "impacts"];

  const sidebarFields = [
    "title_english",
    "title",
    "title_en",
    "location_town",
    "location_region",
    "location_country",
    "lead_org_name",
    "lead_org_type",
    "lead_org_country",
    "website",
    "rdd_1",
    "rdd_2",
    "rdd_3",
    "competencies_required",
    "beneficiaries",
    "id",
  ];

  const excludeFields = [
    "aim",
    "title_en",
    "id",
    "entryId",
    "priorities_ids",
    "prioritiesIds",
    "climatechange_mitigation_ids",
    "climatechange_adaptation_ids",
    "lead_org_type"
  ];

  Object.keys(data).forEach((fieldName) => {
    if (
      excludeFields.includes(fieldName) ||
      sidebarFields.includes(fieldName) ||
      priorityFields.includes(fieldName) ||
      shouldExcludeField(fieldName) ||
      !hasValue(data[fieldName])
    ) {
      return;
    }

    let baseName = fieldName;

    baseName = baseName.replace(/_\d+$/, "");

    baseName = baseName
      .replace(/_details$/, "")
      .replace(/_amount$/, "")
      .replace(/_chars$/, "")
      .replace(/_maturity$/, "")
      .replace(/_description$/, "")
      .replace(/_link$/, "");

    if (!fieldGroups[baseName]) {
      fieldGroups[baseName] = [];
    }

    fieldGroups[baseName].push(fieldName);
  });

  return fieldGroups;
};

const buildSections = (data) => {
  const sections = [];
  const processedFields = new Set();

  if (!data || typeof data !== "object") {
    return sections;
  }

  const priorityFields = ["aims", "story", "impacts"];

  const sidebarFields = [
    "title_english",
    "title",
    "title_en",
    "location_town",
    "location_region",
    "location_country",
    "lead_org_name",
    "lead_org_type",
    "lead_org_country",
    "website",
    "rdd_1",
    "rdd_2",
    "rdd_3",
    "competencies_required",
    "beneficiaries",
    "id",
  ];

  const excludeFields = [
    "aim",
    "title_en",
    "id",
    "entryId",
    "priorities_ids",
    "prioritiesIds",
    "climatechange_mitigation_ids",
    "climatechange_adaptation_ids",
    "lead_org_type"
  ];

  priorityFields.forEach((key) => {
    if (
      hasValue(data[key]) &&
      !processedFields.has(key) &&
      !sidebarFields.includes(key) &&
      !shouldExcludeField(key)
    ) {
      sections.push({
        key,
        title: formatFieldName(key),
        content: data[key],
        color: FIELD_COLOR,
        priority: true,
      });

      processedFields.add(key);
    }
  });

  const fieldGroups = groupFieldsDynamically(data);

  Object.entries(fieldGroups).forEach(([baseName, fieldNames]) => {
    if (fieldNames.every((f) => processedFields.has(f))) {
      return;
    }

    const fields = [];

    fieldNames.forEach((fieldName) => {
      if (
        hasValue(data[fieldName]) &&
        !processedFields.has(fieldName) &&
        !sidebarFields.includes(fieldName) &&
        !excludeFields.includes(fieldName) &&
        !shouldExcludeField(fieldName)
      ) {
        if (typeof data[fieldName] === "boolean") {
          if (data[fieldName]) {
            fields.push({
              label: formatFieldName(fieldName),
              value: "Yes",
            });
          }
        } else {
          fields.push({
            label: formatFieldName(fieldName),
            value: data[fieldName],
            isLink:
              fieldName.includes("link") ||
              fieldName === "website",
          });
        }

        processedFields.add(fieldName);
      }
    });

    if (fields.length > 0) {
      if (
        fields.length === 1 &&
        !fields[0].label.includes("Details")
      ) {
        sections.push({
          key: baseName,
          title: formatFieldName(baseName),
          content: fields[0].value,
          color: FIELD_COLOR,
          isLink: fields[0].isLink,
        });
      } else {
        sections.push({
          key: baseName,
          title: formatFieldName(baseName),
          fields,
          color: FIELD_COLOR,
        });
      }
    }
  });

  Object.keys(data).forEach((key) => {
    if (
      !processedFields.has(key) &&
      !sidebarFields.includes(key) &&
      !excludeFields.includes(key) &&
      !shouldExcludeField(key) &&
      hasValue(data[key])
    ) {
      if (typeof data[key] === "boolean" && !data[key]) {
        return;
      }

      sections.push({
        key,
        title: formatFieldName(key),
        content:
          typeof data[key] === "boolean"
            ? "Yes"
            : data[key],
        color: FIELD_COLOR,
        isLink:
          key.includes("link") || key === "website",
      });

      processedFields.add(key);
    }
  });

  return sections;
};

export {
  buildSections,
  groupFieldsDynamically,
  shouldExcludeField,
  formatFieldName,
  hasValue,
};